import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Timesheet, IConnectGetQuery } from '../../types';
import * as Sentry from '@sentry/react';
import { API, graphqlOperation } from 'aws-amplify';
import { getTimesheet, timesheetsByCustomer } from '../../graphql/queries';
import Loader from '../../Components/Loader';
import { Alert, Button, message } from 'antd';
import TimesheetForm from './partials/TimesheetForm';
import { CreateTimesheetInput, UpdateTimesheetInput } from '../../API';
import { updateTimesheet } from '../../graphql/mutations';
import withAppState, { IAppStateComponentProps } from '../../state/withAppState';
import mixpanel from 'mixpanel-browser';

import { getQueryParameters } from '../../utils/query';
import { LeaveSummary } from 'basealpha-ui';

type Props = RouteComponentProps<{ id: string }, {}, { record: any; breadcrumbs: any }> & IAppStateComponentProps & {};
type State = {
  timesheet?: Timesheet | null;
  loading: boolean;
  error?: string | null;
  user: string;
  leaveSummary?: LeaveSummary | null;
};
class Form extends React.Component<Props, State> {
  state: State = {
    timesheet: null,
    loading: false,
    error: null,
    user: '',
    leaveSummary: undefined,
  };
  getToken = (): string => {
    const params = getQueryParameters();
    return params.token;
  };
  getTimesheet = async (): Promise<any> => {
    const id = this.props.match.params.id;
    const token = this.getToken();
    let timesheet;
    if (!token) {
      this.setState({
        error:
          'The link you have used is invalid. Please check and try again. Alternatively, you can login to your account if you previously signed up and view the timesheet from there.',
      });
      return null;
    }
    try {
      timesheet = await API.get('EhcmsPortalApi', `/timesheet-review/${id}`, {
        queryStringParameters: {
          token,
        },
      });
      return timesheet;
    } catch (e) {
      console.error(e);
      this.setState({
        loading: false,
        error:
          'There was an error retrieving the timesheet you were looking for, please check the link used and try again. Alternatively, you can login to your account if you previously signed up and view the timesheet from there.',
      });
      return;
    }
  };
  async componentDidMount(): Promise<void> {
    if (this.props.match.params.id) {
      // We should load from the API
      try {
        this.setState({
          loading: true,
        });
        const timesheet = await this.getTimesheet();
        if (timesheet) {
          const token = this.getToken();
          let user;
          if (token === timesheet.approver1Token) {
            user = timesheet.approver1;
          }
          if (token === timesheet.approver2Token) {
            user = timesheet.approver2;
          }
          mixpanel.identify(user);
          mixpanel.track('View Timesheet', {
            timesheetId: timesheet.id,
          });
          mixpanel.track('Manager Review Page');
          this.setState({
            timesheet,
            loading: false,
            user,
          });
          if (timesheet?.id && token) {
            API.get('EhcmsPortalApi', `/timesheet-review/${timesheet.id}/leave-summary`, {
              queryStringParameters: { token },
            })
              .then((r: any) => this.setState({ leaveSummary: r?.leaveSummary ?? null }))
              .catch(() => this.setState({ leaveSummary: null }));
          }
        }
      } catch (e) {
        message.error('Timesheet not found');
      }
    }
  }
  save = async (
    values: CreateTimesheetInput & { approving?: boolean; rejecting?: boolean; approverName?: string },
  ): Promise<any> => {
    const token = this.getToken();
    try {
      if (values.rejecting) {
        try {
          const submitResponse = await API.post('EhcmsPortalApi', '/timesheet-review/reject', {
            queryStringParameters: {
              token,
            },
            body: {
              timesheetId: values.id,
              approverName: values.approverName,
            },
          });
          message.success('The timesheet was rejected');
          mixpanel.track('Reject Timesheet', {
            timesheetId: values.id,
          });
          this.props.history.push('/timesheets');
          return submitResponse;
        } catch (e) {
          const error = (e as any).response?.data?.message || 'Error rejecting timesheet';
          message.error(error);
          Sentry.captureException(e);
        }
      }
      const approving = values.approving;
      if (typeof values.approving !== 'undefined') {
        delete values.approving;
      }
      if (approving) {
        try {
          const submitResponse = await API.post('EhcmsPortalApi', '/timesheet-review/approve', {
            queryStringParameters: {
              token,
            },
            body: {
              timesheetId: values.id,
              approverName: values.approverName,
            },
          });
          message.success('The timesheet was approved', 0);
          mixpanel.track('Approve Timesheet', {
            timesheetId: values.id,
          });
          this.props.history.push('/timesheets');
          return submitResponse;
        } catch (e) {
          let error = (e as any).response?.data?.message || 'Error approving timesheet';
          if (
            error ===
            'The number of remaining time off is not sufficient for this time off type.\\nPlease also check the time off waiting for validation.'
          ) {
            error = `You don't have enough leave days available`;
          }
          message.error(error);
          Sentry.captureException(e);
        }
      }
    } catch (e) {
      message.error('Failed to save');
      Sentry.captureException(e);
    }
  };
  close = (): void => {
    this.props.history.push('/');
  };
  render(): React.ReactElement {
    if (this.state.loading) {
      return <Loader />;
    }
    if (this.state.error) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ padding: '40px', maxWidth: '40%', textAlign: 'center' }}>
            <Alert type="error" message={this.state.error} />
            <br />
            <br />
            <Button type="primary" onClick={this.close}>
              Go to Login
            </Button>
          </div>
        </div>
      );
    }
    if (this.state.timesheet) {
      return (
        <TimesheetForm
          record={this.state.timesheet}
          save={this.save}
          close={this.close}
          breadcrumbs={this.props.location.state?.breadcrumbs}
          user={this.state.user}
          leaveSummary={this.state.leaveSummary ?? null}
        />
      );
    }
    return <Loader />;
  }
}

export default withAppState(Form);
